import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { settingsAPI } from '../../services/api';
import useApi from '../../hooks/useApi';
import Loading from '../../components/common/Loading';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { loading, execute: fetchSettings } = useApi(settingsAPI.getSettings);
  const { execute: updateSettings } = useApi(settingsAPI.updateSettings);

  const [formData, setFormData] = useState({
    store: {
      name: '',
      description: '',
      email: '',
      phone: '',
      address: '',
      logo: '',
    },
    payment: {
      stripePublicKey: '',
      stripeSecretKey: '',
      paypalClientId: '',
      paypalSecret: '',
    },
    shipping: {
      freeShippingThreshold: '',
      baseShippingCost: '',
      taxRate: '',
    },
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      youtube: '',
    },
    email: {
      smtpHost: '',
      smtpPort: '',
      smtpUser: '',
      smtpPass: '',
      fromEmail: '',
      fromName: '',
    },
    general: {
      maintenanceMode: false,
      allowRegistration: true,
      requireEmailVerification: true,
      enableGuestCheckout: true,
    },
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetchSettings();
      setSettings(response);
      setFormData(response);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const [section, field] = name.split('.');
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: name.includes('maintenanceMode') ? checked : value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(formData);
      toast.success('Settings updated successfully');
      setIsEditing(false);
      loadSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return <Loading message="Loading settings..." />;
  }

  const renderSection = (title, section, fields) => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Tooltip title={isEditing ? 'Save Changes' : 'Edit Settings'}>
          <IconButton
            color={isEditing ? 'primary' : 'default'}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? <SaveIcon /> : <EditIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {fields.map((field) => (
          <Grid item xs={12} md={6} key={field.name}>
            {field.type === 'switch' ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData[section][field.name]}
                    onChange={handleChange}
                    name={`${section}.${field.name}`}
                    disabled={!isEditing}
                  />
                }
                label={field.label}
              />
            ) : (
              <TextField
                fullWidth
                label={field.label}
                name={`${section}.${field.name}`}
                value={formData[section][field.name]}
                onChange={handleChange}
                disabled={!isEditing}
                type={field.type || 'text'}
                multiline={field.multiline}
                rows={field.rows}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Store Settings
      </Typography>

      <form onSubmit={handleSubmit}>
        {renderSection('Store Information', 'store', [
          { name: 'name', label: 'Store Name' },
          { name: 'description', label: 'Store Description', multiline: true, rows: 3 },
          { name: 'email', label: 'Contact Email' },
          { name: 'phone', label: 'Contact Phone' },
          { name: 'address', label: 'Store Address', multiline: true, rows: 2 },
          { name: 'logo', label: 'Logo URL' },
        ])}

        {renderSection('Payment Settings', 'payment', [
          { name: 'stripePublicKey', label: 'Stripe Public Key' },
          { name: 'stripeSecretKey', label: 'Stripe Secret Key', type: 'password' },
          { name: 'paypalClientId', label: 'PayPal Client ID' },
          { name: 'paypalSecret', label: 'PayPal Secret', type: 'password' },
        ])}

        {renderSection('Shipping Settings', 'shipping', [
          { name: 'freeShippingThreshold', label: 'Free Shipping Threshold ($)', type: 'number' },
          { name: 'baseShippingCost', label: 'Base Shipping Cost ($)', type: 'number' },
          { name: 'taxRate', label: 'Tax Rate (%)', type: 'number' },
        ])}

        {renderSection('Social Media', 'social', [
          { name: 'facebook', label: 'Facebook URL' },
          { name: 'twitter', label: 'Twitter URL' },
          { name: 'instagram', label: 'Instagram URL' },
          { name: 'youtube', label: 'YouTube URL' },
        ])}

        {renderSection('Email Settings', 'email', [
          { name: 'smtpHost', label: 'SMTP Host' },
          { name: 'smtpPort', label: 'SMTP Port', type: 'number' },
          { name: 'smtpUser', label: 'SMTP Username' },
          { name: 'smtpPass', label: 'SMTP Password', type: 'password' },
          { name: 'fromEmail', label: 'From Email' },
          { name: 'fromName', label: 'From Name' },
        ])}

        {renderSection('General Settings', 'general', [
          { name: 'maintenanceMode', label: 'Maintenance Mode', type: 'switch' },
          { name: 'allowRegistration', label: 'Allow User Registration', type: 'switch' },
          { name: 'requireEmailVerification', label: 'Require Email Verification', type: 'switch' },
          { name: 'enableGuestCheckout', label: 'Enable Guest Checkout', type: 'switch' },
        ])}

        {isEditing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              type="submit"
            >
              Save All Changes
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default Settings; 